/**
 * Cutfruit Design System — PDP purchase panel controller (Phase 4).
 * Drives snippets/product-block-ds-purchase-panel.liquid: pack-size segmented
 * control, Subscribe & Save / One-time purchase radio fields, the shared
 * Text Price display above them (Figma: Inputs 39:1421) — its value swaps to
 * match whichever purchase-mode radio is currently selected — and the
 * computed savings line.
 * Framework-independent (delegated listeners), so it works whether or not the
 * surrounding Alpine `Product()` island has finished initializing.
 *
 * Preserves the native selling_plan / quantity contract other theme JS (Alpine
 * Product(), cart drawer) already expects — this script only sets values and
 * dispatches native `change` events + cross-component CustomEvents; it never
 * touches ReCharge or cart JS directly.
 *
 * Events fired on `document`:
 *   pack:change            { detail: { qty } }
 *   purchase-mode:change   { detail: { mode } }  // 'subscribe' | 'onetime'
 */
(function () {
  function formatMoneySafe(cents, format) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // DS "Text Price" component (Figma: Inputs 39:1421): current price splits
  // into whole dollars (no decimals) + superscript cents, e.g. $48 + .96 —
  // the cents superscript is omitted entirely on a whole-dollar amount (e.g.
  // one-time purchase at $72, Figma shows bare "$72", no ".00").
  function splitPrice(cents) {
    const wholeDollars = Math.floor(cents / 100);
    const remainderCents = cents % 100;
    return {
      dollars: `$${wholeDollars}`,
      cents: remainderCents === 0 ? '' : `.${String(remainderCents).padStart(2, '0')}`,
    };
  }

  // Compare-at price (Figma: "$72", no decimals) is always whole dollars,
  // cents truncated — distinct from formatMoneySafe, which keeps cents.
  function formatWholeDollars(cents) {
    return `$${Math.floor(cents / 100)}`;
  }

  function getConfig(panel) {
    const el = panel.querySelector('[data-ds-purchase-panel-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('ds-purchase-panel: invalid config JSON', e);
      return null;
    }
  }

  function currentMode(panel) {
    const checked = panel.querySelector('input[name="purchase_type"]:checked');
    return checked ? checked.value : 'subscribe';
  }

  function currentQty(panel) {
    const input = panel.querySelector('[data-ds-quantity-input]');
    return parseInt((input && input.value) || '1', 10) || 1;
  }

  /**
   * Stacks automatic discounts on top of the live Recharge subscription price,
   * matching real Shopify automatic discounts configured for this product:
   *   1. Recharge selling-plan % (already baked into config.subCents — see
   *      snippets/product-block-ds-purchase-panel.liquid) — sequential stage,
   *      applied to the raw unit price before anything else.
   *   2. First-purchase discount % (any qty, both subscribe and one-time) and
   *      3. Multi-pack discount % (qty >= 2, both subscribe and one-time) —
   *      both Shopify automatic ORDER discounts, same combination class. They
   *      do NOT compound on each other — each is computed against stage 1's
   *      result and the two are summed into one combined rate applied once
   *      (verified against a real cart breakdown: $108 subtotal @ 15%
   *      first-purchase + 5% multi-pack = $108 * (1 - .15 - .05) = $86.40,
   *      not $108 * .85 * .95 = $87.21).
   * These two percentages are merchant-entered settings (ds_purchase_panel
   * block) that should mirror whatever automatic discounts exist in Admin —
   * there is no Liquid/Storefront API that exposes automatic-discount amounts
   * before a line is actually in the cart.
   */
  function applyStackedDiscount(perUnitCents, qty, config) {
    const total = perUnitCents * qty;

    let orderDiscountPct = config.firstPurchaseDiscountPct || 0;
    if (qty >= 2) {
      orderDiscountPct += config.multiPackDiscountPct || 0;
    }

    // Shopify's checkout rounds the customer-owed total UP to the nearest
    // cent. Match that so the PDP price always agrees with what checkout
    // actually charges.
    return Math.ceil(total * (1 - orderDiscountPct / 100));
  }

  function updatePrices(panel) {
    const config = getConfig(panel);
    if (!config) return;

    const qty = currentQty(panel);
    const mode = currentMode(panel);

    const subUnit = config.subCents || 0;
    const subCompareUnit = config.subCompareCents || config.oneTimeCents || 0;
    const oneTimeUnit = config.oneTimeCents || 0;

    const subTotal = applyStackedDiscount(subUnit, qty, config);
    const subCompareTotal = subCompareUnit * qty;
    const oneTimeTotal = applyStackedDiscount(oneTimeUnit, qty, config);
    const oneTimeCompareTotal = oneTimeUnit * qty;

    const subCard = panel.querySelector('[data-ds-radio-card][data-value="subscribe"]');
    const tagEl = subCard && subCard.querySelector('.ds-radio-card__tag');
    if (tagEl && subCompareTotal > 0) {
      const subPct = Math.round(((subCompareTotal - subTotal) / subCompareTotal) * 100);
      tagEl.textContent = `${subPct}% OFF`;
    }

    // Shared "Text Price" (Figma: Inputs 39:1421) shows whichever mode is
    // currently selected — subscribe's discounted total or one-time's full price.
    const isSubscribe = mode === 'subscribe';
    const displayTotal = isSubscribe ? subTotal : oneTimeTotal;
    const displayCompareTotal = isSubscribe ? subCompareTotal : oneTimeCompareTotal;
    const showCompare = isSubscribe ? subCompareTotal > 0 : oneTimeCompareTotal > oneTimeTotal;

    const priceCurrentEl = panel.querySelector('[data-ds-text-price-current]');
    const priceDecimalsEl = panel.querySelector('[data-ds-text-price-decimals]');
    const priceCompareEl = panel.querySelector('[data-ds-text-price-compare]');
    const split = splitPrice(displayTotal);
    if (priceCurrentEl) priceCurrentEl.textContent = split.dollars;
    if (priceDecimalsEl) priceDecimalsEl.textContent = split.cents;
    if (priceCompareEl) {
      if (showCompare) {
        priceCompareEl.textContent = formatWholeDollars(displayCompareTotal);
        priceCompareEl.hidden = false;
      } else {
        priceCompareEl.hidden = true;
      }
    }

    const subMeta = panel.querySelector('[data-ds-sub-meta]');
    if (subMeta) {
      const pouches = qty * 12;
      const gummies = pouches * 12;
      const pattern =
        config.subscribeMetaPattern ||
        'Auto-ships [pouches] pouches ([gummies] gummies) every 4 weeks, beginning after your first order ships';
      subMeta.textContent = pattern
        .replace('[pouches]', pouches)
        .replace('[gummies]', gummies);
    }

    const ongoingLine = panel.querySelector('[data-ds-ongoing-copy]');
    if (ongoingLine && subCompareTotal > 0) {
      const todayPct = Math.round(((subCompareTotal - subTotal) / subCompareTotal) * 100);
      const ongoingTotal = subUnit * qty;
      const ongoingPct = Math.round(((subCompareTotal - ongoingTotal) / subCompareTotal) * 100);
      ongoingLine.textContent = `${todayPct}% off today, then ${ongoingPct}% off ongoing`;
    }

    const savingsLine = panel.querySelector('[data-ds-savings-line]');
    if (savingsLine) {
      const referenceTotal = mode === 'subscribe' ? subCompareTotal : oneTimeCompareTotal;
      const currentTotal = mode === 'subscribe' ? subTotal : oneTimeTotal;
      const savings = Math.max(0, referenceTotal - currentTotal);
      if (savings > 0 && referenceTotal > 0) {
        const pct = Math.round((savings / referenceTotal) * 100);
        savingsLine.textContent = `You're saving ${formatMoneySafe(savings, config.moneyFormat)} (${pct}% off)`;
        savingsLine.hidden = false;
      } else {
        savingsLine.hidden = true;
      }
    }
  }

  function setPurchaseMode(panel, mode) {
    const sellingPlanInput = panel.querySelector('[data-ds-selling-plan-input]');
    const cards = panel.querySelectorAll('[data-ds-radio-card]');

    cards.forEach((card) => {
      const isMatch = card.getAttribute('data-value') === mode;
      card.classList.toggle('ds-radio-card--checked', isMatch);
      const input = card.querySelector('.ds-radio-card__input');
      if (input) input.checked = isMatch;
    });

    if (sellingPlanInput) {
      if (mode === 'subscribe' && sellingPlanInput.value) {
        sellingPlanInput.disabled = false;
      } else {
        sellingPlanInput.disabled = true;
      }
    }

    updatePrices(panel);
    document.dispatchEvent(new CustomEvent('purchase-mode:change', { detail: { mode } }));
  }

  function setPackQty(panel, qty) {
    const qtyInput = panel.querySelector('[data-ds-quantity-input]');
    if (qtyInput) {
      qtyInput.value = qty;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const segments = panel.querySelectorAll('[data-ds-segmented-control-segment]');
    segments.forEach((segment) => {
      const input = segment.querySelector('.ds-segmented-control__input');
      if (input) input.checked = segment.getAttribute('data-value') === String(qty);
    });

    updatePrices(panel);
    document.dispatchEvent(new CustomEvent('pack:change', { detail: { qty } }));
  }

  function initPanel(panel) {
    if (panel.dataset.dsPurchasePanelInit) return;
    panel.dataset.dsPurchasePanelInit = '1';

    updatePrices(panel);

    panel.addEventListener('change', (event) => {
      if (event.target.matches('input[name="purchase_type"]')) {
        setPurchaseMode(panel, event.target.value);
      }
      if (event.target.matches('.ds-segmented-control__input')) {
        const segment = event.target.closest('[data-ds-segmented-control-segment]');
        const qty = segment ? segment.getAttribute('data-value') : '1';
        setPackQty(panel, parseInt(qty, 10));
      }
    });

    // Keyboard: segmented-control segments and radio cards are native
    // radio inputs, so Space/Enter/arrow-key behavior is native for free.
  }

  function initAll() {
    document.querySelectorAll('[data-ds-purchase-panel]').forEach(initPanel);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  // Re-init if the section is re-rendered (theme editor).
  document.addEventListener('shopify:section:load', initAll);

  // Re-broadcast the theme's variant-change event as the DS-namespaced
  // `variant:change` CustomEvent (per docs/design-system-migration/04-pdp.md's
  // cross-component events contract), for any DS component that wants to react
  // without coupling directly to theme:variant:change.
  document.addEventListener('theme:variant:change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
  });
})();
